import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Badge } from "@/shared/components/ui/badge";
import {
  PackageCheck,
  CreditCard,
  User,
  Shirt,
  Wrench,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { InvoicePaymentMethod, RentalType } from "@/shared/types/enums";
import { formatCurrency, formatDate } from "@/shared/lib/formatters";
import type { RentalResponseDTO } from "@/shared/types/rental.types";
import type { ReturnDTO } from "../types";

const PAYMENT_LABELS: Record<InvoicePaymentMethod, string> = {
  [InvoicePaymentMethod.CASH]: "Dinheiro",
  [InvoicePaymentMethod.CREDIT_CARD]: "Cartão de crédito",
  [InvoicePaymentMethod.DEBIT_CARD]: "Cartão de débito",
  [InvoicePaymentMethod.PIX]: "PIX",
  [InvoicePaymentMethod.BANK_TRANSFER]: "Transferência bancária",
};

interface ReturnModalProps {
  open: boolean;
  rental: RentalResponseDTO | null;
  loading: boolean;
  warning?: string | null;
  onConfirm: (data: ReturnDTO) => void;
  onClose: () => void;
}

export const ReturnModal = ({
  open,
  rental,
  loading,
  warning,
  onConfirm,
  onClose,
}: ReturnModalProps) => {
  const [selectedStockIds, setSelectedStockIds] = useState<Set<number>>(
    new Set(),
  );
  const [gownReturnQtys, setGownReturnQtys] = useState<Record<number, number>>(
    {},
  );
  const [maintenanceDays, setMaintenanceDays] = useState("1");
  const [hasPayment, setHasPayment] = useState(false);
  const [paidValue, setPaidValue] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<InvoicePaymentMethod | "">(
    "",
  );
  const [invoiceObservation, setInvoiceObservation] = useState("");

  const handleClose = () => {
    setSelectedStockIds(new Set());
    setGownReturnQtys({});
    setMaintenanceDays("1");
    setHasPayment(false);
    setPaidValue("");
    setPaymentMethod("");
    setInvoiceObservation("");
    onClose();
  };

  const toggleStock = (id: number) => {
    setSelectedStockIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const dto: ReturnDTO = {
      stockIds:
        rental?.rentalType === RentalType.SUIT
          ? Array.from(selectedStockIds)
          : undefined,
      gownItems:
        rental?.rentalType === RentalType.GOWN
          ? Object.entries(gownReturnQtys)
              .filter(([, qty]) => qty > 0)
              .map(([productId, quantity]) => ({
                productId: Number(productId),
                quantity,
              }))
          : undefined,
      maintenanceDays:
        rental?.rentalType === RentalType.SUIT && maintenanceDays
          ? Number(maintenanceDays)
          : undefined,
      payments:
        hasPayment && paidValue && paymentMethod
          ? [{ paidValue: parseFloat(paidValue), paymentMethod }]
          : [],
      ...(invoiceObservation.trim()
        ? { invoiceObservation: invoiceObservation.trim() }
        : {}),
    };
    onConfirm(dto);
  };

  const isSuit = rental?.rentalType === RentalType.SUIT;
  const totalStocks = rental?.stockItems?.length ?? 0;
  const canConfirm = isSuit
    ? selectedStockIds.size === totalStocks && totalStocks > 0
    : true;
  const remaining = rental ? rental.totalValue - rental.paidValue : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: "#09090b",
          border: "1px solid #27272a",
          borderRadius: "0.75rem",
        }}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full"
              style={{
                backgroundColor: "#14532d20",
                border: "1px solid #22c55e",
              }}
            >
              <PackageCheck className="w-5 h-5" style={{ color: "#22c55e" }} />
            </div>
            <div>
              <DialogTitle style={{ color: "#f4f4f5", fontSize: "1.15rem" }}>
                Registrar devolução
              </DialogTitle>
              <DialogDescription style={{ color: "#a1a1aa" }}>
                Confirmar retorno das peças ao estoque
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {rental && (
          <div className="space-y-4">
            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#14532d10",
                border: "1px solid #22c55e40",
              }}
            >
              <div
                className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide"
                style={{ color: "#22c55e" }}
              >
                <User className="w-3.5 h-3.5" /> Detalhes do aluguel
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Contrato
                  </p>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "#f4f4f5" }}
                  >
                    {rental.contractNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Cliente
                  </p>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "#f4f4f5" }}
                  >
                    {rental.clientName}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Saída
                  </p>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#e5e7eb" }}
                  >
                    {formatDate(rental.expectedOutAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Retorno previsto
                  </p>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#e5e7eb" }}
                  >
                    {formatDate(rental.expectedInAt)}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="rounded-lg p-4 space-y-2"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <div
                className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide"
                style={{ color: "#a1a1aa" }}
              >
                <FileText className="w-3.5 h-3.5" /> Situação financeira
              </div>
              <div className="grid grid-cols-3 gap-3 pt-1">
                <div
                  className="text-center rounded-md py-2"
                  style={{ backgroundColor: "#09090b" }}
                >
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Total
                  </p>
                  <p
                    className="text-base font-bold"
                    style={{ color: "#3b82f6" }}
                  >
                    {formatCurrency(rental.totalValue)}
                  </p>
                </div>
                <div
                  className="text-center rounded-md py-2"
                  style={{ backgroundColor: "#09090b" }}
                >
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Pago
                  </p>
                  <p
                    className="text-base font-bold"
                    style={{ color: "#22c55e" }}
                  >
                    {formatCurrency(rental.paidValue)}
                  </p>
                </div>
                <div
                  className="text-center rounded-md py-2"
                  style={{ backgroundColor: "#09090b" }}
                >
                  <p className="text-xs" style={{ color: "#71717a" }}>
                    Restante
                  </p>
                  <p
                    className="text-base font-bold"
                    style={{ color: remaining > 0 ? "#f97316" : "#22c55e" }}
                  >
                    {formatCurrency(remaining)}
                  </p>
                </div>
              </div>

              {warning && (
                <div
                  className="flex items-start gap-2.5 rounded-md px-3 py-2.5 mt-1"
                  style={{
                    backgroundColor: "#78350f20",
                    border: "1px solid #f9731640",
                  }}
                >
                  <AlertTriangle
                    className="w-4 h-4 shrink-0 mt-0.5"
                    style={{ color: "#f97316" }}
                  />
                  <p
                    className="text-xs leading-relaxed"
                    style={{ color: "#fdba74" }}
                  >
                    {warning}
                  </p>
                </div>
              )}
            </div>

            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide"
                  style={{ color: "#a1a1aa" }}
                >
                  <Shirt className="w-3.5 h-3.5" />{" "}
                  {isSuit ? "Peças do terno" : "Itens de beca"}
                </div>
                {isSuit && (
                  <div className="flex items-center gap-3">
                    <span
                      className="text-xs"
                      style={{ color: canConfirm ? "#22c55e" : "#f97316" }}
                    >
                      {selectedStockIds.size} de {totalStocks} selecionada(s){" "}
                      {!canConfirm && "— todas obrigatórias"}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const all = (rental.stockItems ?? []).map((s) => s.id);
                        setSelectedStockIds(
                          selectedStockIds.size === all.length
                            ? new Set()
                            : new Set(all),
                        );
                      }}
                      className="text-xs underline"
                      style={{ color: "#3b82f6" }}
                    >
                      {selectedStockIds.size === totalStocks
                        ? "Desmarcar todas"
                        : "Selecionar todas"}
                    </button>
                  </div>
                )}
              </div>

              {isSuit ? (
                <div className="space-y-1.5">
                  {(rental.stockItems ?? []).map((stockItem) => {
                    const selected = selectedStockIds.has(stockItem.id);
                    return (
                      <label
                        key={stockItem.id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded cursor-pointer"
                        style={{
                          backgroundColor: selected ? "#14532d20" : "#09090b",
                          border: `1px solid ${selected ? "#22c55e50" : "#3f3f46"}`,
                        }}
                      >
                        <div className="relative shrink-0">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleStock(stockItem.id)}
                            className="sr-only"
                          />
                          <div
                            className="w-4 h-4 rounded border flex items-center justify-center"
                            style={{
                              backgroundColor: selected
                                ? "#22c55e"
                                : "transparent",
                              borderColor: selected ? "#22c55e" : "#52525b",
                            }}
                          >
                            {selected && (
                              <svg
                                className="w-2.5 h-2.5"
                                viewBox="0 0 10 10"
                                fill="none"
                              >
                                <path
                                  d="M1.5 5L4 7.5L8.5 2.5"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        <Shirt
                          className="w-3.5 h-3.5 shrink-0"
                          style={{ color: selected ? "#22c55e" : "#71717a" }}
                        />
                        <span
                          className="text-sm flex-1"
                          style={{ color: selected ? "#e5e7eb" : "#a1a1aa" }}
                        >
                          <span className="font-medium">
                            {stockItem.productName}
                          </span>
                          <span
                            className="ml-2 text-xs"
                            style={{ color: selected ? "#86efac" : "#52525b" }}
                          >
                            #{stockItem.id} · {stockItem.barcode} · Tam.{" "}
                            {stockItem.size}
                          </span>
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium shrink-0"
                          style={{
                            backgroundColor: selected ? "#14532d40" : "#27272a",
                            color: selected ? "#22c55e" : "#71717a",
                          }}
                        >
                          {selected ? "Devolvendo" : "Pendente"}
                        </span>
                      </label>
                    );
                  })}

                  <div
                    className="flex items-center gap-3 px-3 py-2.5 rounded mt-1"
                    style={{
                      backgroundColor: "#09090b",
                      border: "1px solid #3f3f46",
                    }}
                  >
                    <Wrench
                      className="w-3.5 h-3.5 shrink-0"
                      style={{ color: "#a1a1aa" }}
                    />
                    <label
                      className="text-sm flex-1"
                      style={{ color: "#e5e7eb" }}
                    >
                      Dias de manutenção
                    </label>
                    <Input
                      type="number"
                      min={1}
                      value={maintenanceDays}
                      onChange={(e) => setMaintenanceDays(e.target.value)}
                      className="w-20 h-8 text-center"
                      style={{
                        backgroundColor: "#18181b",
                        borderColor: "#3f3f46",
                        color: "#f4f4f5",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {rental.gownItems?.map((item) => {
                    const pending = item.quantity - item.returnedQuantity;
                    const returning = gownReturnQtys[item.productId] ?? 0;
                    const returnedAfter = item.returnedQuantity + returning;
                    return (
                      <div
                        key={item.idGownRentalItem}
                        className="rounded-lg overflow-hidden"
                        style={{ border: "1px solid #3f3f46" }}
                      >
                        <div
                          className="px-3 py-2.5 flex items-center justify-between"
                          style={{ backgroundColor: "#09090b" }}
                        >
                          <span
                            className="text-sm font-medium"
                            style={{ color: "#e5e7eb" }}
                          >
                            Produto #{item.productId}
                          </span>
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: "#22c55e20",
                                color: "#22c55e",
                              }}
                            >
                              {item.returnedQuantity} devolvido(s)
                            </span>
                            {pending > 0 && (
                              <span
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{
                                  backgroundColor: "#f9731620",
                                  color: "#f97316",
                                }}
                              >
                                {pending} pendente(s)
                              </span>
                            )}
                          </div>
                        </div>

                        <div
                          className="px-3 py-2 space-y-2"
                          style={{ backgroundColor: "#0f0f10" }}
                        >
                          <div
                            className="flex items-center justify-between text-xs"
                            style={{ color: "#71717a" }}
                          >
                            <span>
                              Total:{" "}
                              <strong style={{ color: "#e5e7eb" }}>
                                {item.quantity}
                              </strong>
                            </span>
                            <span>
                              Após esta devolução:{" "}
                              <strong
                                style={{
                                  color:
                                    returnedAfter === item.quantity
                                      ? "#22c55e"
                                      : "#e5e7eb",
                                }}
                              >
                                {returnedAfter}/{item.quantity}
                              </strong>
                            </span>
                          </div>

                          <div
                            className="w-full rounded-full overflow-hidden"
                            style={{
                              height: "4px",
                              backgroundColor: "#27272a",
                            }}
                          >
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${(returnedAfter / item.quantity) * 100}%`,
                                backgroundColor:
                                  returnedAfter === item.quantity
                                    ? "#22c55e"
                                    : "#3b82f6",
                              }}
                            />
                          </div>

                          <div className="flex items-center justify-between pt-0.5">
                            <label
                              className="text-xs font-medium"
                              style={{ color: "#a1a1aa" }}
                            >
                              Devolver agora
                              {pending > 0 && (
                                <span style={{ color: "#71717a" }}>
                                  {" "}
                                  (máx. {pending})
                                </span>
                              )}
                            </label>
                            <Input
                              type="number"
                              min={0}
                              max={pending}
                              value={returning}
                              onChange={(e) =>
                                setGownReturnQtys((p) => ({
                                  ...p,
                                  [item.productId]: Number(e.target.value),
                                }))
                              }
                              className="w-20 h-8 text-center"
                              style={{
                                backgroundColor: "#18181b",
                                borderColor: "#3f3f46",
                                color: "#f4f4f5",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              className="rounded-lg p-4 space-y-3"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={hasPayment}
                    onChange={(e) => setHasPayment(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className="w-4 h-4 rounded border flex items-center justify-center transition-colors"
                    style={{
                      backgroundColor: hasPayment ? "#22c55e" : "transparent",
                      borderColor: hasPayment ? "#22c55e" : "#52525b",
                    }}
                  >
                    {hasPayment && (
                      <svg
                        className="w-2.5 h-2.5"
                        viewBox="0 0 10 10"
                        fill="none"
                      >
                        <path
                          d="M1.5 5L4 7.5L8.5 2.5"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard
                    className="w-4 h-4"
                    style={{ color: "#a1a1aa" }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: "#e5e7eb" }}
                  >
                    Registrar pagamento na devolução
                  </span>
                </div>
              </label>

              {hasPayment && (
                <div
                  className="grid grid-cols-2 gap-3 pt-1"
                  style={{
                    borderTop: "1px solid #27272a",
                    paddingTop: "0.75rem",
                  }}
                >
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-medium"
                      style={{ color: "#a1a1aa" }}
                    >
                      Valor (R$)
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0,00"
                      value={paidValue}
                      onChange={(e) => setPaidValue(e.target.value)}
                      style={{
                        backgroundColor: "#09090b",
                        borderColor: "#3f3f46",
                        color: "#f4f4f5",
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-medium"
                      style={{ color: "#a1a1aa" }}
                    >
                      Forma de pagamento
                    </label>
                    <select
                      value={paymentMethod}
                      onChange={(e) =>
                        setPaymentMethod(e.target.value as InvoicePaymentMethod)
                      }
                      className="h-10 w-full rounded-md border px-3 text-sm"
                      style={{
                        backgroundColor: "#09090b",
                        borderColor: "#3f3f46",
                        color: "#f4f4f5",
                      }}
                    >
                      <option value="">Selecione</option>
                      {Object.values(InvoicePaymentMethod).map((m) => (
                        <option key={m} value={m}>
                          {PAYMENT_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div
              className="rounded-lg p-4 space-y-2"
              style={{
                backgroundColor: "#18181b",
                border: "1px solid #27272a",
              }}
            >
              <label
                className="text-xs font-medium uppercase tracking-wide block"
                style={{ color: "#a1a1aa" }}
              >
                Observação da NF
              </label>
              <Textarea
                placeholder="Descrição que aparece na nota fiscal"
                rows={3}
                maxLength={2000}
                value={invoiceObservation}
                onChange={(e) => setInvoiceObservation(e.target.value)}
                style={{
                  backgroundColor: "#09090b",
                  borderColor: "#3f3f46",
                  color: "#f4f4f5",
                }}
              />
              <p className="text-xs" style={{ color: "#71717a" }}>
                Opcional. Em branco usa o padrão da loja.
              </p>
            </div>

            <div
              className="rounded-lg px-4 py-3 flex items-center gap-3"
              style={{
                backgroundColor: "#14532d10",
                border: "1px solid #22c55e40",
              }}
            >
              <PackageCheck
                className="w-4 h-4 shrink-0"
                style={{ color: "#22c55e" }}
              />
              <p className="text-sm" style={{ color: "#86efac" }}>
                Ao confirmar, as peças selecionadas retornam ao estoque e o
                status passará para{" "}
                <Badge
                  style={{
                    backgroundColor: "#14532d20",
                    color: "#22c55e",
                    border: "1px solid #22c55e40",
                    fontSize: "0.7rem",
                  }}
                >
                  Finalizado
                </Badge>
                .
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            style={{ borderColor: "#3f3f46", color: "#a1a1aa" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              loading ||
              !canConfirm ||
              (hasPayment && (!paidValue || !paymentMethod))
            }
            style={{ backgroundColor: "#22c55e", color: "#fff" }}
            className="gap-2"
          >
            <PackageCheck className="w-4 h-4" />
            {loading ? "Processando..." : "Confirmar devolução"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
